import React from "react";
import { AlertsDataToggleSwitch } from "./AlertsDataToggleSwitch";
import { CircularProgress } from "@mui/material";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { useDashboardStore } from "./store/dashboardStore";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import RefreshIcon from "@mui/icons-material/Refresh";

export const DataUsageGraph = () => {
  const {
    usageData,
    currentData,
    setCurrentData,
    dateRangeName,
    setDateRangeName,
    dataIsLoading,
    setDataIsLoading,
    errorFetchingUsageData,
    setTypeString,
    setDateRangeString,
    getDataUsageData,
  } = useDashboardStore();

  const retryText = (
    <>
      <RefreshIcon fontSize="small" />
      &nbsp;Retry
    </>
  );

  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [last5AnchorEl, setLast5AnchorEl] = React.useState<null | HTMLElement>(
    null
  );

  const [last5Name, setLast5Name] = React.useState("Data Usage");

  const open = Boolean(anchorEl);

  const last5Open = Boolean(last5AnchorEl);

  const [rangeChoices, setRangeChoices] = React.useState([]);

  const dataUsageAlertVolumeArray = [
    { name: "Data Usage", value: "bandwidth" },
    { name: "Alert Volume", value: "alert_volume" },
  ];

  React.useEffect(() => {
    if (localStorage.rangeChoices) {
      setRangeChoices(JSON.parse(localStorage.rangeChoices));
    }
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLast5Click = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLast5AnchorEl(event.currentTarget);
  };

  const handleLast5Close = () => {
    setLast5AnchorEl(null);
  };

  return (
    <div className="w-full min-w-0 bg-white px-4 pt-4 pb-5 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-actuate-blue font-mulish text-[26px] font-bold">
          Data Usage vs Alert Volume
        </p>
        <div className="text-actuate-blue font-mulish text-[16px]">
          <Button
            id="time-frame-selector"
            aria-controls={open ? "time-frame-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            {dateRangeName}
          </Button>
          <Menu
            id="time-frame-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "time-frame-selector",
            }}
          >
            {rangeChoices.map((rangeChoiceObject: any) => {
              return (
                <MenuItem
                  key={rangeChoiceObject.value}
                  onClick={() => {
                    const timeChoiceValues = new Map();
                    dataUsageAlertVolumeArray.forEach((option: any) => {
                      timeChoiceValues.set(
                        `${option.name}`,
                        `${option.value}`
                      );
                    });
                    setDateRangeName(rangeChoiceObject.name);
                    setDateRangeString(rangeChoiceObject.value);
                    setDataIsLoading(true);
                    handleClose();
                    getDataUsageData(navigate);
                  }}
                >
                  {rangeChoiceObject.name}
                </MenuItem>
              );
            })}
          </Menu>
        </div>
      </div>
      <div className="flex flex-wrap items-center">
        <p className="text-actuate-blue font-mulish text-[16px]">
          Sort Top 5 Sites by
        </p>
        <div className="text-actuate-blue font-mulish text-[16px]">
          <Button
            id="sort-type-selector"
            aria-controls={last5Open ? "sort-type-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={last5Open ? "true" : undefined}
            onClick={handleLast5Click}
          >
            {last5Name}
          </Button>
          <Menu
            id="sort-type-menu"
            anchorEl={last5AnchorEl}
            open={last5Open}
            onClose={handleLast5Close}
            MenuListProps={{
              "aria-labelledby": "sort-type-selector",
            }}
          >
            {dataUsageAlertVolumeArray.map((object: any) => {
              return (
                <MenuItem
                  key={object.value}
                  onClick={() => {
                    handleClose();
                    handleLast5Close();
                    const rangeChoiceValues = new Map();
                    rangeChoices.forEach((option: any) => {
                      rangeChoiceValues.set(
                        `${option.name}`,
                        `${option.value}`
                      );
                    });
                    if (object.value === "alert_volume") {
                      setCurrentData("alertVolumeData");
                    }
                    if (object.value === "bandwidth") {
                      setCurrentData("usageData");
                    }
                    setTypeString(object.value);
                    setLast5Name(object.name);
                    setDataIsLoading(true);
                    getDataUsageData(navigate);
                  }}
                >
                  {object.name}
                </MenuItem>
              );
            })}
          </Menu>
        </div>
      </div>
      {!errorFetchingUsageData && (
        <div>
          <div className="pt-2">
            <AlertsDataToggleSwitch />
          </div>
          {!dataIsLoading && (
            <div className="mt-3 flex w-full items-stretch">
              <div className="flex shrink-0 items-center">
                <p
                  className="text-[#757575] font-mulish text-[16px] rotate-180"
                  style={{ writingMode: "vertical-rl" }}
                >
                  {currentData === "usageData"
                    ? "Real Time Bandwidth Usage GB"
                    : "Number of Alerts"}
                </p>
              </div>
              <div className="h-[260px] min-w-0 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  {currentData === "usageData" ? (
                    <BarChart
                      data={usageData}
                      margin={{
                        top: 5,
                        right: 20,
                        left: 20,
                        bottom: 20,
                      }}
                      onClick={(event: any) => {
                        navigate(
                          `/sites/aboutsite/${event.activePayload[0].payload.id}`
                        );
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="display_name" type="category" hide />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar
                        cursor="pointer"
                        dataKey="kbs"
                        fill="#FF8400"
                        name="GB"
                        onClick={(event) =>
                          navigate(`/sites/aboutsite/${event.id}`)
                        }
                      >
                        <LabelList
                          dataKey="abreviated_name"
                          position="bottom"
                        />
                      </Bar>
                    </BarChart>
                  ) : (
                    <BarChart
                      data={usageData}
                      margin={{
                        top: 5,
                        right: 20,
                        left: 20,
                        bottom: 20,
                      }}
                      onClick={(event: any) => {
                        navigate(
                          `/sites/aboutsite/${event.activePayload[0].payload.id}`
                        );
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="display_name" type="category" hide />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar
                        cursor="pointer"
                        dataKey="alert_volume"
                        fill="#283E6D"
                        name="Alerts"
                        onClick={(event) =>
                          navigate(`/sites/aboutsite/${event.id}`)
                        }
                      >
                        <LabelList
                          dataKey="abreviated_name"
                          position="bottom"
                        />
                      </Bar>
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {!dataIsLoading && (
            <p className="pt-2 text-center text-[#757575] font-mulish text-[16px]">
              {last5Name === "Data Usage"
                ? usageData.length > 1
                  ? `${usageData.length} sites with Highest Data Usage`
                  : `Site with highest Data Usage`
                : usageData.length > 1
                ? `${usageData.length} sites with Highest Alert Volume`
                : `Site with highest Alert Volume`}
            </p>
          )}
          {dataIsLoading && (
            <div className="flex h-[260px] flex-col items-center justify-center">
              <CircularProgress size={25} />
              <div className="font-mulish text-actuate-blue test-bold pt-2">
                Fetching Data...
              </div>
            </div>
          )}
        </div>
      )}
      {errorFetchingUsageData && !dataIsLoading && (
        <div className="flex h-[260px] flex-col items-center justify-center">
          <div className="font-mulish text-actuate-blue test-bold text-[20px]">
            Error fetching data.
          </div>
          <OrangeTextButton
            onClick={() => {
              setDataIsLoading(true);
              getDataUsageData(navigate);
            }}
            text={retryText}
            disabled={false}
          />
        </div>
      )}
    </div>
  );
};
